// src/pages/configuracion/SistemaPage.tsx
import { useState } from 'react';
import { Card, Button, Badge, Spinner, Switch, Input, Label } from '@/components/ui';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import { toast } from '@/components/ui/Toast';
import { Ticket, Activity, BookOpen, RefreshCw, Boxes, Plus } from 'lucide-react';
import {
  useTiposTicket,
  useTiposActividad,
  useCategoriasConocimiento,
  useActualizarTipoTicket,
  useActualizarTipoActividad,
  useActualizarCategoriaConocimiento,
  useModulosConfig,
  useCrearModulo,
  useActualizarModulo,
  useDesactivarModulo,
} from '@/hooks/queries/useConfiguracion';
import type { TipoTicketConfig, TipoActividadConfig, CategoriaConocimientoConfig, ModuloConfig } from '@/types';

export function SistemaPage() {
  const [activeTab, setActiveTab] = useState('tickets');

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Configuracion del Sistema</h1>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="tickets">
            <Ticket className="h-4 w-4 mr-2" />
            Tipos de Ticket
          </TabsTrigger>
          <TabsTrigger value="actividades">
            <Activity className="h-4 w-4 mr-2" />
            Tipos de Actividad
          </TabsTrigger>
          <TabsTrigger value="conocimiento">
            <BookOpen className="h-4 w-4 mr-2" />
            Categorias
          </TabsTrigger>
          <TabsTrigger value="modulos">
            <Boxes className="h-4 w-4 mr-2" />
            Módulos
          </TabsTrigger>
        </TabsList>

        <TabsContent value="tickets">
          <TiposTicketSection />
        </TabsContent>

        <TabsContent value="actividades">
          <TiposActividadSection />
        </TabsContent>

        <TabsContent value="conocimiento">
          <CategoriasSection />
        </TabsContent>

        <TabsContent value="modulos">
          <ModulosSection />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function TiposTicketSection() {
  const { data: tipos, isLoading, refetch } = useTiposTicket();
  const actualizarMutation = useActualizarTipoTicket();

  const handleToggleActivo = async (tipo: TipoTicketConfig) => {
    try {
      await actualizarMutation.mutateAsync({
        id: tipo.id,
        data: { activo: !tipo.activo },
      });
      toast.success(`Tipo ${tipo.activo ? 'desactivado' : 'activado'} correctamente`);
    } catch {
      // Error handled by global MutationCache.onError
    }
  };

  if (isLoading) {
    return <div className="flex justify-center py-12"><Spinner size="lg" /></div>;
  }

  return (
    <Card className="mt-4">
      <div className="p-4 border-b flex items-center justify-between">
        <h3 className="font-semibold text-gray-900">Tipos de Ticket</h3>
        <Button variant="ghost" size="sm" onClick={() => refetch()}>
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>
      <div className="divide-y">
        {tipos?.map((tipo) => (
          <div key={tipo.id} className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div
                className="w-4 h-4 rounded-full"
                style={{ backgroundColor: tipo.color || '#6B7280' }}
              />
              <div>
                <p className="font-medium text-gray-900">{tipo.nombre}</p>
                <p className="text-sm text-gray-500">Codigo: {tipo.codigo}</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Badge variant="default">Orden: {tipo.orden}</Badge>
              <Switch
                checked={tipo.activo}
                onCheckedChange={() => handleToggleActivo(tipo)}
              />
            </div>
          </div>
        ))}
        {!tipos?.length && (
          <div className="p-8 text-center text-gray-500">
            No hay tipos de ticket configurados
          </div>
        )}
      </div>
    </Card>
  );
}

function TiposActividadSection() {
  const { data: tipos, isLoading, refetch } = useTiposActividad();
  const actualizarMutation = useActualizarTipoActividad();

  const handleToggleActivo = async (tipo: TipoActividadConfig) => {
    try {
      await actualizarMutation.mutateAsync({
        id: tipo.id,
        data: { activo: !tipo.activo },
      });
      toast.success(`Tipo ${tipo.activo ? 'desactivado' : 'activado'} correctamente`);
    } catch {
      // Error handled by global MutationCache.onError
    }
  };

  if (isLoading) {
    return <div className="flex justify-center py-12"><Spinner size="lg" /></div>;
  }

  return (
    <Card className="mt-4">
      <div className="p-4 border-b flex items-center justify-between">
        <h3 className="font-semibold text-gray-900">Tipos de Actividad</h3>
        <Button variant="ghost" size="sm" onClick={() => refetch()}>
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>
      <div className="divide-y">
        {tipos?.map((tipo) => (
          <div key={tipo.id} className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div
                className="w-4 h-4 rounded-full"
                style={{ backgroundColor: tipo.color || '#6B7280' }}
              />
              <div>
                <p className="font-medium text-gray-900">{tipo.nombre}</p>
                <p className="text-sm text-gray-500">
                  {tipo.codigo}
                  {tipo.area_default && ` • ${tipo.area_default}`}
                  {tipo.aliases && tipo.aliases.length > 0 && ` • ${tipo.aliases.join(', ')}`}
                </p>
              </div>
            </div>
            <Switch
              checked={tipo.activo}
              onCheckedChange={() => handleToggleActivo(tipo)}
            />
          </div>
        ))}
        {!tipos?.length && (
          <div className="p-8 text-center text-gray-500">
            No hay tipos de actividad configurados
          </div>
        )}
      </div>
    </Card>
  );
}

function CategoriasSection() {
  const { data: categorias, isLoading, refetch } = useCategoriasConocimiento();
  const actualizarMutation = useActualizarCategoriaConocimiento();

  const handleToggleActivo = async (cat: CategoriaConocimientoConfig) => {
    try {
      await actualizarMutation.mutateAsync({
        id: cat.id,
        data: { activo: !cat.activo },
      });
      toast.success(`Categoria ${cat.activo ? 'desactivada' : 'activada'} correctamente`);
    } catch {
      // Error handled by global MutationCache.onError
    }
  };

  if (isLoading) {
    return <div className="flex justify-center py-12"><Spinner size="lg" /></div>;
  }

  return (
    <Card className="mt-4">
      <div className="p-4 border-b flex items-center justify-between">
        <h3 className="font-semibold text-gray-900">Categorias de Conocimiento</h3>
        <Button variant="ghost" size="sm" onClick={() => refetch()}>
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>
      <div className="divide-y">
        {categorias?.map((cat) => (
          <div key={cat.id} className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div
                className="w-4 h-4 rounded-full"
                style={{ backgroundColor: cat.color || '#6B7280' }}
              />
              <div>
                <p className="font-medium text-gray-900">{cat.nombre}</p>
                {cat.descripcion && (
                  <p className="text-sm text-gray-500">{cat.descripcion}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Badge variant="default">Orden: {cat.orden}</Badge>
              <Switch
                checked={cat.activo}
                onCheckedChange={() => handleToggleActivo(cat)}
              />
            </div>
          </div>
        ))}
        {!categorias?.length && (
          <div className="p-8 text-center text-gray-500">
            No hay categorias configuradas
          </div>
        )}
      </div>
    </Card>
  );
}


function ModulosSection() {
  const { data: modulos, isLoading, refetch } = useModulosConfig();
  const crearMutation = useCrearModulo();
  const actualizarMutation = useActualizarModulo();
  const desactivarMutation = useDesactivarModulo();
  const [showForm, setShowForm] = useState(false);
  const [newCodigo, setNewCodigo] = useState('');
  const [newNombre, setNewNombre] = useState('');
  const [newGrupo, setNewGrupo] = useState('ERP');

  const handleCrear = async () => {
    if (!newCodigo.trim() || !newNombre.trim()) return;
    try {
      await crearMutation.mutateAsync({
        codigo: newCodigo.trim().toUpperCase(),
        nombre: newNombre.trim(),
        grupo: newGrupo,
      });
      toast.success('Módulo creado correctamente');
      setNewCodigo('');
      setNewNombre('');
      setShowForm(false);
    } catch {
      // Error handled by global MutationCache.onError
    }
  };

  const handleToggleActivo = async (modulo: ModuloConfig) => {
    try {
      if (modulo.activo) {
        await desactivarMutation.mutateAsync(modulo.id);
        toast.success('Módulo desactivado');
      } else {
        await actualizarMutation.mutateAsync({ id: modulo.id, data: { activo: true } });
        toast.success('Módulo reactivado');
      }
    } catch {
      // Error handled by global MutationCache.onError
    }
  };

  if (isLoading) {
    return <div className="flex justify-center py-12"><Spinner size="lg" /></div>;
  }

  const grupos = [...new Set(modulos?.map((m) => m.grupo) ?? [])];

  return (
    <Card className="mt-4">
      <div className="p-4 border-b flex items-center justify-between">
        <h3 className="font-semibold text-gray-900">Módulos del Sistema</h3>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4" />
          </Button>
          <Button size="sm" onClick={() => setShowForm(!showForm)}>
            <Plus className="h-4 w-4 mr-1" />
            Nuevo
          </Button>
        </div>
      </div>

      {showForm && (
        <div className="p-4 border-b bg-gray-50 space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Código</Label>
              <Input
                value={newCodigo}
                onChange={(e) => setNewCodigo(e.target.value)}
                placeholder="MODULO"
                className="text-sm"
              />
            </div>
            <div>
              <Label className="text-xs">Nombre</Label>
              <Input
                value={newNombre}
                onChange={(e) => setNewNombre(e.target.value)}
                placeholder="Nombre del módulo"
                className="text-sm"
              />
            </div>
            <div>
              <Label className="text-xs">Grupo</Label>
              <select
                value={newGrupo}
                onChange={(e) => setNewGrupo(e.target.value)}
                className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="ERP">ERP</option>
                <option value="SISTEMAS">SISTEMAS</option>
                <option value="OTROS">OTROS</option>
              </select>
            </div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={handleCrear} loading={crearMutation.isPending}>
              Crear
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setShowForm(false)}>
              Cancelar
            </Button>
          </div>
        </div>
      )}

      {grupos.map((grupo) => (
        <div key={grupo}>
          <div className="px-4 py-2 bg-gray-50 border-b">
            <span className="text-xs font-semibold text-gray-500 uppercase">{grupo}</span>
          </div>
          <div className="divide-y">
            {modulos
              ?.filter((m) => m.grupo === grupo)
              .map((modulo) => (
                <div key={modulo.id} className="p-4 flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900">{modulo.nombre}</p>
                    <p className="text-sm text-gray-500">{modulo.codigo}</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <Badge variant="default">Orden: {modulo.orden}</Badge>
                    <Switch
                      checked={modulo.activo}
                      onCheckedChange={() => handleToggleActivo(modulo)}
                    />
                  </div>
                </div>
              ))}
          </div>
        </div>
      ))}

      {!modulos?.length && (
        <div className="p-8 text-center text-gray-500">
          No hay módulos configurados
        </div>
      )}
    </Card>
  );
}
